/**
 * Serenity API Client
 * Provides typed functions for interacting with the Serenity API server
 */
import { LiveKitTokenData } from "@/components/ChatSessionProvider";
import config from "@/config";
import { brand } from "@/constants";
import { ClientInfo } from "@/store/types";

/**
 * Reason codes returned in the body of a 429 from any rate-limit ring.
 * Stable HTTP-API contract — copy is keyed off these values; do not rename.
 * Mirrors `DenialReason` in server/app/common/rate_limit/errors.py.
 */
export type RateLimitReason =
  | "ip_rate_limit"
  | "user_rate_limit"
  | "concurrent_session"
  | "session_hourly"
  | "session_daily"
  | "capacity";

/**
 * Body of a 429 response from any rate-limit ring.
 * `retryAfter` is null when the wait is bounded only by another user action
 * (e.g. ending a session on another device for `concurrent_session`).
 */
export interface RateLimitBody {
  reason: RateLimitReason;
  retryAfter: number | null;
}

/**
 * Body of a 423 response when the user is inside the crisis cooldown.
 * Returned only by the session endpoints. Resources are inlined so the
 * client can render the Pause screen on first attempt with no extra fetch.
 *
 * Mirrors `to_423_response` in server/app/common/crisis/errors.py.
 */
export interface CrisisCooldownBody {
  reason: "crisis_cooldown";
  resumeAt?: string;
  resources: {
    lifeline: { label: string; phone: string; sms: string };
    crisisTextLine: {
      label: string;
      smsKeyword: string;
      smsNumber: string;
    };
    emergency: { label: string; phone: string };
    protocolUrl: string;
  };
}

/**
 * Common failure shape for every API call. `rateLimit` is set when the
 * server returned 429 with the documented body; `crisisCooldown` is set
 * when the server returned 423 with the documented body. `message` always
 * carries a user-facing string for the generic-error code path — including
 * the network case (when fetch itself threw before reaching the server),
 * for which `request()` produces an actionable network-specific copy
 * instead of a generic "something went wrong".
 */
export type ApiFailure = {
  success: false;
  message: string;
  rateLimit?: RateLimitBody;
  crisisCooldown?: CrisisCooldownBody;
};

export type AuthValidateResult =
  { success: true } | (ApiFailure & { waitlisted?: boolean });

export type CreateSessionResult =
  { success: true; sessionId: string } | ApiFailure;

export type GetSessionResult =
  { success: true; payload: LiveKitTokenData } | ApiFailure;

export type DeleteResult = { success: true } | ApiFailure;
export type AddToAllowlistResult = { success: true } | ApiFailure;
export type JoinWaitlistResult = { success: true } | ApiFailure;
export type SubmitFeedbackResult =
  { success: true; feedbackId: string } | ApiFailure;

const RATE_LIMIT_COPY: Record<RateLimitReason, string> = {
  session_hourly:
    "You've reached your hourly limit for new sessions. Please wait a bit before starting another.",
  session_daily:
    "You've reached your daily session limit. Take some time to reflect. We'll be ready for you again tomorrow.",
  concurrent_session:
    "You already have another active session. Please end that session first, or wait a few minutes for it to automatically expire.",
  capacity: `${brand.name} is currently at full capacity. Please try again in a few minutes.`,
  user_rate_limit:
    "You're making requests a bit too quickly. Please wait a moment before trying again.",
  ip_rate_limit:
    "We're receiving unusually high traffic from your network. Please try again in a few minutes."
};

const GENERIC_RATE_LIMIT_MESSAGE =
  "You've reached a system limit. Please wait a moment before trying again.";

/**
 * Translate a 429 body to the user-facing string.
 */
export function rateLimitMessage(body: RateLimitBody): string {
  return RATE_LIMIT_COPY[body.reason] ?? GENERIC_RATE_LIMIT_MESSAGE;
}

async function parseRateLimitBody(
  response: Response
): Promise<RateLimitBody | null> {
  try {
    const data = await response.json();
    if (data && typeof data.reason === "string") {
      return data as RateLimitBody;
    }
  } catch {
    // Cloud Armor 429s have no body — fall through.
  }
  return null;
}

async function rateLimitFailure(response: Response): Promise<ApiFailure> {
  const body = await parseRateLimitBody(response);
  return {
    success: false,
    message: body ? rateLimitMessage(body) : GENERIC_RATE_LIMIT_MESSAGE,
    rateLimit: body ?? undefined
  };
}

const GENERIC_CRISIS_COOLDOWN_MESSAGE = `${brand.name} is paused for a short time. Please use the resources shown.`;

async function parseCrisisCooldownBody(
  response: Response
): Promise<CrisisCooldownBody | null> {
  try {
    const data = await response.json();
    if (
      data &&
      data.reason === "crisis_cooldown" &&
      typeof data.resumeAt === "string" &&
      data.resources
    ) {
      return data as CrisisCooldownBody;
    }
  } catch {
    // Malformed body — fall through to generic copy.
  }
  return null;
}

async function crisisCooldownFailure(response: Response): Promise<ApiFailure> {
  const body = await parseCrisisCooldownBody(response);
  return {
    success: false,
    message: GENERIC_CRISIS_COOLDOWN_MESSAGE,
    crisisCooldown: body ?? undefined
  };
}

function buildUrl(path: string): string {
  const { hostPort } = config.serenityApi;
  // Everything except localhost goes over HTTPS.
  const scheme = hostPort.startsWith("localhost") ? "http" : "https";
  return `${scheme}://${hostPort}${path}`;
}

type HttpMethod = "GET" | "POST" | "DELETE" | "PUT" | "PATCH";

interface RequestOptions<TSuccess> {
  method: HttpMethod;
  path: string;
  /** Bearer token. Omit for unauthenticated endpoints. */
  idToken?: string;
  /** JSON body. Omit for GET/DELETE without payload. */
  body?: unknown;
  /** Prefix for the catch-all "Failed to X: 500 …" message. */
  errorPrefix: string;
  /** Per-status overrides for non-429 failures (e.g. 403, 422). */
  statusMessages?: Partial<Record<number, string>>;
  /** Parse a 2xx response into success-payload fields. Defaults to none. */
  parseOk?: (response: Response) => Promise<TSuccess>;
}

/**
 * Shared transport layer: builds the request, normalizes successes,
 * 429s, per-status overrides, generic non-2xx, and network errors into
 * `ApiResult<TSuccess>`. Endpoint functions become thin wrappers.
 */
async function request<TSuccess = Record<string, never>>(
  opts: RequestOptions<TSuccess>
): Promise<({ success: true } & TSuccess) | ApiFailure> {
  try {
    const headers: Record<string, string> = {};
    if (opts.idToken) headers.Authorization = `Bearer ${opts.idToken}`;
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";

    const response = await fetch(buildUrl(opts.path), {
      method: opts.method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined
    });

    if (response.ok) {
      const data = opts.parseOk
        ? await opts.parseOk(response)
        : ({} as TSuccess);
      return { success: true, ...data };
    }

    if (response.status === 429) return rateLimitFailure(response);
    if (response.status === 423) return crisisCooldownFailure(response);

    const override = opts.statusMessages?.[response.status];
    if (override) return { success: false, message: override };

    return {
      success: false,
      message: `${opts.errorPrefix}: ${response.status} ${response.statusText}`
    };
  } catch {
    // fetch() threw — DNS, TCP, TLS, offline, middlebox dropping the
    // upgrade, etc. We never reached a server response, so we own the copy
    // here rather than echoing the raw browser error.
    return {
      success: false,
      message:
        "Couldn't reach the server. Please check your network and try again."
    };
  }
}

/** Validates user authentication with server. */
export async function validateAuth(
  idToken: string
): Promise<AuthValidateResult> {
  const result = await request({
    method: "POST",
    path: "/auth/validate",
    idToken,
    errorPrefix: "Authorization check failed. Please try again.",
    statusMessages: { 403: "Not enrolled" }
  });
  // Existing caller convention: a 403 means "not on the allowlist; sent to
  // waitlist instead." Tag the failure so the UI can branch.
  if (!result.success && result.message === "Not enrolled") {
    return { ...result, waitlisted: true };
  }
  return result;
}

/**
 * Creates a new chat session.
 *
 * @param patternName - When set, seeds a pattern-focused session (launched
 *   from a Pattern card) so the agent opens on that pattern.
 */
export function createSession(
  idToken: string,
  patternName?: string
): Promise<CreateSessionResult> {
  return request<{ sessionId: string }>({
    method: "POST",
    path: "/sessions",
    idToken,
    body: patternName ? { patternName } : undefined,
    errorPrefix: "Failed to create session",
    parseOk: async (r) => ({ sessionId: (await r.json()).sessionId })
  });
}

/** Gets session data including LiveKit connection token. */
export function getSession(
  sessionId: string,
  idToken: string
): Promise<GetSessionResult> {
  return request<{ payload: LiveKitTokenData }>({
    method: "GET",
    path: `/sessions/${sessionId}`,
    idToken,
    errorPrefix: "Failed to get session",
    parseOk: async (r) => ({ payload: await r.json() })
  });
}

/** Deletes a session and its associated data. */
export function deleteSession(
  sessionId: string,
  idToken: string
): Promise<DeleteResult> {
  return request({
    method: "DELETE",
    path: `/sessions/${sessionId}`,
    idToken,
    errorPrefix: "Failed to delete session"
  });
}

/** Deletes a single user fact. */
export function deleteFact(
  factName: string,
  idToken: string
): Promise<DeleteResult> {
  return request({
    method: "DELETE",
    path: `/facts/${factName}`,
    idToken,
    errorPrefix: "Failed to delete fact"
  });
}

/** Deletes the user's account and all associated data. */
export function deleteAccount(idToken: string): Promise<DeleteResult> {
  return request({
    method: "DELETE",
    path: "/account",
    idToken,
    errorPrefix: "Failed to delete account"
  });
}

/** Adds an email to the allowlist. Requires admin access. */
export function addToAllowlist(
  email: string,
  idToken: string
): Promise<AddToAllowlistResult> {
  return request({
    method: "POST",
    path: "/admin/allowlist",
    idToken,
    body: { email },
    errorPrefix: "Failed to add to allowlist",
    statusMessages: {
      403: "Admin access required",
      422: "Invalid email address"
    }
  });
}

/** Adds an email to the public waitlist. No authentication required. */
export function joinWaitlist(
  email: string,
  honeypot: string = ""
): Promise<JoinWaitlistResult> {
  return request({
    method: "POST",
    path: "/waitlist",
    body: { email, honeypot },
    errorPrefix: "Something went wrong. Please try again.",
    statusMessages: { 422: "Please enter a valid email address." }
  });
}

/**
 * Payload for submitting user feedback to the server.
 */
export interface SubmitFeedbackPayload {
  text: string;
  feedbackTypes: string[];
  sessionId?: string;
  screenName?: string;
  /** Firestore id of the reported conversation turn the feedback is about.
   *  The server decides the surrounding context to include. */
  anchorMessageId?: string;
  clientInfo: Partial<ClientInfo>;
  clientVersion: string;
}

/** Submits user feedback. */
export function submitFeedback(
  idToken: string,
  payload: SubmitFeedbackPayload
): Promise<SubmitFeedbackResult> {
  return request<{ feedbackId: string }>({
    method: "POST",
    path: "/feedback",
    idToken,
    body: payload,
    errorPrefix: "Failed to submit feedback",
    parseOk: async (r) => ({ feedbackId: (await r.json()).feedbackId })
  });
}
